import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, Package, TrendingDown, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#C3002F', '#06b6d4', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

const fmtPrice = (val) => {
  if (val == null) return '-';
  return `₹${Number(val).toLocaleString()} INR`;
};
const fmtPct = (val, d = 1) => (val != null ? `${Number(val).toFixed(d)}%` : '-');

const CustomTooltip = ({ active, payload, label, isDark, noCurrency }) => {
  if (active && payload && payload.length) {
    return (
      <div className={`${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} border p-3 rounded shadow-xl`}>
        <p className={`${isDark ? 'text-white' : 'text-zinc-900'} font-medium mb-1`}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' && !noCurrency ? `₹${entry.value.toLocaleString()} INR` : entry.value?.toLocaleString?.() ?? entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: PieChartIcon },
  { id: 'pricing', label: 'Pricing', icon: BarChart3 },
  { id: 'revenue', label: 'Revenue', icon: TrendingUp },
  { id: 'parts', label: 'Part Analytics', icon: Package },
];

export default function AnalyticsPage() {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [familyData, setFamilyData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [pricingDistribution, setPricingDistribution] = useState([]);
  const [topParts, setTopParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState('');
  const [priceHistory, setPriceHistory] = useState(null);
  const [partDetails, setPartDetails] = useState(null);
  const [partDataLoading, setPartDataLoading] = useState(false);
  const [competitorPricing, setCompetitorPricing] = useState(null);
  const [competitorLoading, setCompetitorLoading] = useState(false);
  const [revenueTrends, setRevenueTrends] = useState(null);
  const [revenueTrendsLoading, setRevenueTrendsLoading] = useState(true);
  const [revenueTrendsYears, setRevenueTrendsYears] = useState(5);
  const [revenueTrendsLimit, setRevenueTrendsLimit] = useState(10);
  const [topPartsPageSize, setTopPartsPageSize] = useState(10);
  const [topPartsPage, setTopPartsPage] = useState(1);

  useEffect(() => {
    fetchAnalytics();
    fetchParts();
  }, []);

  useEffect(() => {
    fetchRevenueTrends(revenueTrendsYears, revenueTrendsLimit);
  }, [revenueTrendsYears, revenueTrendsLimit]);

  const fetchAnalytics = async () => {
    try {
      const [summaryRes, familyRes, trendRes, statusRes, pricingRes, topPartsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/analytics/by-family`),
        axios.get(`${API}/analytics/approval-trend`),
        axios.get(`${API}/analytics/status-breakdown`),
        axios.get(`${API}/analytics/pricing-distribution`),
        axios.get(`${API}/analytics/top-parts?n=100&limit=100`)
      ]);
      setSummary(summaryRes.data);
      setFamilyData(familyRes.data);
      setTrendData(trendRes.data);
      setStatusBreakdown(statusRes.data);
      setPricingDistribution(pricingRes.data);
      console.log('pricingRes.data :', pricingRes.data);
      setTopParts(topPartsRes.data?.items ?? topPartsRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchParts = async () => {
    try {
      const res = await axios.get(`${API}/parts/search?q=`);
      setParts(res.data);
    } catch (error) {
      toast.error('Failed to load parts list');
    }
  };

  const fetchRevenueTrends = async (years, limit) => {
    setRevenueTrendsLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/revenue-trends?years=${years}&limit=${limit}`);
      setRevenueTrends(res.data);
    } catch (error) {
      toast.error('Failed to load revenue trends');
    } finally {
      setRevenueTrendsLoading(false);
    }
  };

  const fetchPartData = async (partNumber) => {
    if (!partNumber) {
      setPriceHistory(null);
      setPartDetails(null);
      setCompetitorPricing(null);
      return;
    }
    setPartDataLoading(true);
    setCompetitorPricing(null);
    setCompetitorLoading(true);

    // Fire competitor pricing independently — it can take 5-10s
    axios.get(`${API}/analytics/part/${encodeURIComponent(partNumber)}/competitor-pricing`)
      .then(res => setCompetitorPricing(res.data))
      .catch(() => setCompetitorPricing(null))
      .finally(() => setCompetitorLoading(false));

    try {
      const [historyRes, detailsRes] = await Promise.all([
        axios.get(`${API}/analytics/by-part-history?part_number=${partNumber}`),
        axios.get(`${API}/analytics/part/${encodeURIComponent(partNumber)}/detailed`)
      ]);
      setPriceHistory(historyRes.data);
      setPartDetails(detailsRes.data);
    } catch (error) {
      toast.error('Failed to load part data');
      setPriceHistory(null);
      setPartDetails(null);
    } finally {
      setPartDataLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
        </div>
      </MainLayout>
    );
  }

  const statusData = [
    { name: 'Approved', value: summary?.approved_cards || 0, color: '#10b981' },
    { name: 'Pending', value: summary?.pending_approvals || 0, color: '#f59e0b' },
    { name: 'Rejected', value: summary?.rejected_cards || 0, color: '#ef4444' },
    { name: 'Draft', value: (summary?.total_cost_cards || 0) - (summary?.approved_cards || 0) - (summary?.pending_approvals || 0) - (summary?.rejected_cards || 0), color: '#52525b' }
  ].filter(d => d.value > 0);

  const gridColor = isDark ? '#27272a' : '#e4e4e7';
  const axisColor = isDark ? '#52525b' : '#a1a1aa';
  const labelColor = isDark ? '#a1a1aa' : '#52525b';

  const topPartsTotalPages = Math.ceil(topParts.length / topPartsPageSize);
  const topPartsSlice = topParts.slice((topPartsPage - 1) * topPartsPageSize, topPartsPage * topPartsPageSize);

  return (
    <MainLayout>
      <div className="p-8 space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-cyan-500 dark:text-cyan-400" />
            Analytics
          </h1>
          <p className="text-zinc-500 dark:text-zinc-400 mt-1">Pricing performance and trends analysis</p>
        </div>

        {/* Tab Bar */}
        <div className="border-b border-zinc-200 dark:border-zinc-800">
          <nav className="flex gap-1" aria-label="Analytics tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === id
                  ? 'border-[#C3002F] text-[#C3002F]'
                  : 'border-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:border-zinc-300 dark:hover:border-zinc-600'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Tab: Overview ── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Summary KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase">Total Active Cards</p>
                    <p className="text-2xl font-mono font-bold text-zinc-900 dark:text-white">{summary?.total_cost_cards || 0}</p>
                    <p className="text-xs text-zinc-500 mt-1">{summary?.total_parts || 0} parts • {summary?.total_families || 0} families</p>
                  </div>
                </div>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70 uppercase">Total Revenue</p>
                    <p className="text-2xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{fmtPrice(summary?.total_potential_revenue || 0)}</p>
                    <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70 mt-1">From {summary?.approved_cards || 0} approved cards</p>
                  </div>
                </div>
              </div>
              <div className="p-5 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  <div>
                    <p className="text-xs text-cyan-600/70 dark:text-cyan-400/70 uppercase">Avg Dealer Price</p>
                    <p className="text-2xl font-mono font-bold text-cyan-600 dark:text-cyan-400">{fmtPrice(summary?.avg_dealer_price || 0)}</p>
                    <p className="text-xs text-cyan-600/70 dark:text-cyan-400/70 mt-1">{summary?.avg_dealer_markup_pct || 0}% markup</p>
                  </div>
                </div>
              </div>
              <div className="p-5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <PieChartIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="text-xs text-amber-600/70 dark:text-amber-400/70 uppercase">Approval Rate</p>
                    <p className="text-2xl font-mono font-bold text-amber-600 dark:text-amber-400">
                      {summary?.total_cost_cards ? Math.round((summary.approved_cards / summary.total_cost_cards) * 100) : 0}%
                    </p>
                    <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-1">{summary?.pending_approvals || 0} pending review</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg">
                <p className="text-xs text-emerald-600 dark:text-emerald-400 uppercase mb-1">Approved</p>
                <p className="text-xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{summary?.approved_cards || 0}</p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg">
                <p className="text-xs text-blue-600 dark:text-blue-400 uppercase mb-1">In Review</p>
                <p className="text-xl font-mono font-bold text-blue-600 dark:text-blue-400">{summary?.in_review_cards || 0}</p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
                <p className="text-xs text-amber-600 dark:text-amber-400 uppercase mb-1">Pending</p>
                <p className="text-xl font-mono font-bold text-amber-600 dark:text-amber-400">{summary?.pending_approvals || 0}</p>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg">
                <p className="text-xs text-orange-600 dark:text-orange-400 uppercase mb-1">On Hold</p>
                <p className="text-xl font-mono font-bold text-orange-600 dark:text-orange-400">{summary?.on_hold_cards || 0}</p>
              </div>
              <div className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                <p className="text-xs text-red-600 dark:text-red-400 uppercase mb-1">Rejected</p>
                <p className="text-xl font-mono font-bold text-red-600 dark:text-red-400">{summary?.rejected_cards || 0}</p>
              </div>
            </div>

            {/* Status Distribution + Cards Over Time */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Cost Card Status Distribution</h3>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: axisColor }}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-zinc-500">No data available</div>
                )}
              </div>

              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Cards Created Over Time</h3>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis dataKey="date" stroke={axisColor} tick={{ fontSize: 10, fill: labelColor }} />
                      <YAxis stroke={axisColor} />
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Legend />
                      <Line type="monotone" dataKey="approved" name="Approved" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="in_review" name="In Review" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="pending" name="Pending" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-zinc-500">No data available</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Pricing ── */}
        {activeTab === 'pricing' && (
          <div className="space-y-8">


            {/* Average Pricing by Family — full width */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Average Pricing by Family</h3>
              {familyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={familyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="family" stroke={axisColor} tick={{ fontSize: 11, fill: labelColor }} angle={-45} textAnchor="end" height={80} />
                    <YAxis stroke={axisColor} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k INR`} />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Legend />
                    <Bar dataKey="avg_dealer_price" name="Avg Dealer Price" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="avg_mrp" name="Avg MRP" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-zinc-500">No data available</div>
              )}
            </div>

            {/* Family Performance Details Table */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Family Performance Details</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Family</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Cards</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Avg Dealer Price</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Avg MRP</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {familyData.length > 0 ? familyData.map((row, index) => (
                      <tr key={index} className="border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
                        <td className="py-3 px-4 text-zinc-900 dark:text-white">{row.family}</td>
                        <td className="py-3 px-4 font-mono text-zinc-700 dark:text-zinc-300">{row.count}</td>
                        <td className="py-3 px-4 font-mono text-cyan-600 dark:text-cyan-400">{fmtPrice(row.avg_dealer_price)}</td>
                        <td className="py-3 px-4 font-mono text-amber-600 dark:text-amber-400">{fmtPrice(row.avg_mrp)}</td>
                        <td className="py-3 px-4 font-mono text-emerald-600 dark:text-emerald-400">{fmtPrice(row.total_revenue)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-zinc-500">
                          No analytics data available. Create and approve cost cards to see analytics.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Price Range Distribution */}
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Price Range Distribution</h3>
                {pricingDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={pricingDistribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis dataKey="range" stroke={axisColor} tick={{ fontSize: 11, fill: labelColor }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke={axisColor} />
                      <Tooltip content={<CustomTooltip isDark={isDark} noCurrency />} />
                      <Bar dataKey="count" name="Number of Parts" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-zinc-500">No data available</div>
                )}
              </div>

              {/* Placeholder to balance grid — full-width chart below */}
              <div className="hidden lg:block" />
            </div>

          </div>
        )}

        {/* ── Tab: Revenue ── */}
        {activeTab === 'revenue' && (
          <div className="space-y-8">
            {/* Revenue by Part Family */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Revenue by Part Family</h3>
              {familyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={familyData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis type="number" stroke={axisColor} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k INR`} />
                    <YAxis dataKey="family" type="category" stroke={axisColor} width={100} tick={{ fontSize: 12, fill: labelColor }} />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Bar dataKey="total_revenue" name="Total Revenue" fill="#C3002F" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-zinc-500">No data available</div>
              )}
            </div>

            {/* Revenue Trends */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white tracking-tight flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-emerald-500 dark:text-emerald-400" />
                    Revenue Trends
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400 mt-1">Parts with the highest price growth and decline</p>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 uppercase mb-1">Period</label>
                    <select
                      value={revenueTrendsYears}
                      onChange={e => setRevenueTrendsYears(Number(e.target.value))}
                      className="h-9 px-3 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C3002F]/30"
                    >
                      {[1, 2, 3, 4, 5].map(y => <option key={y} value={y}>{y} Year{y > 1 ? 's' : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 uppercase mb-1">Top Count</label>
                    <select
                      value={revenueTrendsLimit}
                      onChange={e => setRevenueTrendsLimit(Number(e.target.value))}
                      className="h-9 px-3 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C3002F]/30"
                    >
                      {[5, 10, 20, 30, 40, 50].map(l => <option key={l} value={l}>{l} parts</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {revenueTrendsLoading ? (
                <div className="flex items-center justify-center py-12 gap-3 text-zinc-500">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C3002F]" />
                  <span>Loading revenue trends...</span>
                </div>
              ) : revenueTrends ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="p-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Parts Analyzed</p>
                      <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{revenueTrends.summary?.total_parts_analyzed ?? '-'}</p>
                    </div>
                    <div className="p-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg">
                      <p className="text-xs text-zinc-500 uppercase mb-1">With Changes</p>
                      <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{revenueTrends.summary?.parts_with_price_changes ?? '-'}</p>
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 uppercase mb-1">Growth Parts</p>
                      <p className="text-xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{revenueTrends.summary?.parts_with_growth ?? '-'}</p>
                    </div>
                    <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                      <p className="text-xs text-red-600 dark:text-red-400 uppercase mb-1">Decline Parts</p>
                      <p className="text-xl font-mono font-bold text-red-600 dark:text-red-400">{revenueTrends.summary?.parts_with_decline ?? '-'}</p>
                    </div>
                    <div className="p-3 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded-lg">
                      <p className="text-xs text-cyan-600 dark:text-cyan-400 uppercase mb-1">Avg Growth</p>
                      <p className="text-xl font-mono font-bold text-cyan-600 dark:text-cyan-400">{fmtPct(revenueTrends.summary?.avg_growth_percent)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h3 className="text-base font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Top Growth
                      </h3>
                      {revenueTrends.top_growth?.map(part => (
                        <div key={part.part_number} className="p-4 bg-emerald-50/50 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/20 rounded-lg space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-mono text-sm text-zinc-900 dark:text-white font-semibold">{part.part_number}</p>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{part.family_name} · {part.total_approvals} approval{part.total_approvals !== 1 ? 's' : ''}</p>
                            </div>
                            <span className="shrink-0 text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20 px-2 py-0.5 rounded">
                              +{fmtPct(part.revenue_change_percent)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-zinc-500">
                            <span>{fmtPrice(part.first_approved_price)} → {fmtPrice(part.latest_approved_price)}</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-mono">+{fmtPrice(part.revenue_change)}</span>
                          </div>
                          {part.monthly_breakdown?.length > 1 && (
                            <ResponsiveContainer width="100%" height={130}>
                              <LineChart data={part.monthly_breakdown} margin={{ top: 4, right: 8, left: 8, bottom: 28 }}>
                                <XAxis dataKey="month" stroke={axisColor} tick={{ fontSize: 9, fill: labelColor }} label={{ value: 'Month', position: 'insideBottom', offset: -16, fontSize: 10, fill: labelColor }} />
                                <YAxis stroke={axisColor} tick={{ fontSize: 9, fill: labelColor }} width={48} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} label={{ value: 'MRP', angle: -90, position: 'insideLeft', offset: 12, fontSize: 10, fill: labelColor }} />
                                <Line type="monotone" dataKey="avg_mrp" stroke="#10b981" strokeWidth={2} dot={false} />
                                <Tooltip content={({ active, payload }) => active && payload?.length ? (
                                  <div className={`${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} border p-2 rounded shadow text-xs`}>
                                    <p className="font-medium">{payload[0].payload.month}</p>
                                    <p style={{ color: '#10b981' }}>MRP: {fmtPrice(payload[0].value)}</p>
                                  </div>
                                ) : null} />
                              </LineChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-base font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4" /> Top Decline
                      </h3>
                      {revenueTrends.top_decline?.map(part => (
                        <div key={part.part_number} className="p-4 bg-red-50/50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/20 rounded-lg space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-mono text-sm text-zinc-900 dark:text-white font-semibold">{part.part_number}</p>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{part.family_name} · {part.total_approvals} approval{part.total_approvals !== 1 ? 's' : ''}</p>
                            </div>
                            <span className="shrink-0 text-xs font-mono font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20 px-2 py-0.5 rounded">
                              {fmtPct(part.revenue_change_percent)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-zinc-500">
                            <span>{fmtPrice(part.first_approved_price)} → {fmtPrice(part.latest_approved_price)}</span>
                            <span className="text-red-600 dark:text-red-400 font-mono">{fmtPrice(part.revenue_change)}</span>
                          </div>
                          {part.monthly_breakdown?.length > 1 && (
                            <ResponsiveContainer width="100%" height={130}>
                              <LineChart data={part.monthly_breakdown} margin={{ top: 4, right: 8, left: 8, bottom: 28 }}>
                                <XAxis dataKey="month" stroke={axisColor} tick={{ fontSize: 9, fill: labelColor }} label={{ value: 'Month', position: 'insideBottom', offset: -16, fontSize: 10, fill: labelColor }} />
                                <YAxis stroke={axisColor} tick={{ fontSize: 9, fill: labelColor }} width={48} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} label={{ value: 'MRP', angle: -90, position: 'insideLeft', offset: 12, fontSize: 10, fill: labelColor }} />
                                <Line type="monotone" dataKey="avg_mrp" stroke="#ef4444" strokeWidth={2} dot={false} />
                                <Tooltip content={({ active, payload }) => active && payload?.length ? (
                                  <div className={`${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} border p-2 rounded shadow text-xs`}>
                                    <p className="font-medium">{payload[0].payload.month}</p>
                                    <p style={{ color: '#ef4444' }}>MRP: {fmtPrice(payload[0].value)}</p>
                                  </div>
                                ) : null} />
                              </LineChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Tab: Part Analytics ── */}
        {activeTab === 'parts' && (
          <div className="space-y-8">

            {/* Part Deep-Dive */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6">
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white tracking-tight flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-[#C3002F]" />
                    Part Analytics
                  </h2>
                  <p className="text-zinc-500 dark:text-zinc-400 mt-1">Select a part to view price history and detailed breakdown</p>
                </div>

                {/* Part Selector */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 max-w-sm">
                    <label className="block text-xs font-medium text-zinc-500 uppercase mb-1">Select Part</label>
                    <select
                      value={selectedPart}
                      onChange={(e) => { setSelectedPart(e.target.value); fetchPartData(e.target.value); }}
                      className="w-full h-10 px-3 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C3002F]/30"
                    >
                      <option value="">— Select a part —</option>
                      {parts.map(p => (
                        <option key={p.id} value={p.part_number}>{p.part_number} — {p.name}</option>
                      ))}
                    </select>
                  </div>
                  {partDataLoading && (
                    <div className="mt-5">
                      <div className="animate-spin w-5 h-5 border-2 border-[#C3002F] border-t-transparent rounded-full" />
                    </div>
                  )}
                </div>

                {!partDataLoading && selectedPart && !priceHistory && !partDetails && (
                  <div className="py-8 text-center text-zinc-500">No approved data found for this part.</div>
                )}

                {/* Price History */}
                {priceHistory?.parts?.length > 0 && (() => {
                  const ph = priceHistory.parts[0];
                  const chartData = ph.price_history.map(h => ({
                    date: h.date,
                    DBP: h.dbp,
                    MRP: h.mrp,
                    COGS: h.cogs,
                  }));
                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                          <p className="text-xs text-zinc-500 uppercase mb-1">First Price</p>
                          <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{fmtPrice(ph.first_price)}</p>
                        </div>
                        <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                          <p className="text-xs text-zinc-500 uppercase mb-1">Latest Price</p>
                          <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{fmtPrice(ph.latest_price)}</p>
                        </div>
                        <div className={`p-4 border rounded-lg ${(ph.price_change ?? 0) >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20' : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20'}`}>
                          <p className={`text-xs uppercase mb-1 ${(ph.price_change ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>Price Change</p>
                          <p className={`text-xl font-mono font-bold ${(ph.price_change ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                            {ph.price_change != null ? `${ph.price_change >= 0 ? '+' : ''}${fmtPrice(ph.price_change)} (${fmtPct(ph.price_change_percent, 2)})` : '-'}
                          </p>
                        </div>
                        <div className="p-4 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded-lg">
                          <p className="text-xs text-cyan-600 dark:text-cyan-400 uppercase mb-1">Avg GP%</p>
                          <p className="text-xl font-mono font-bold text-cyan-600 dark:text-cyan-400">{fmtPct(ph.avg_gp_percent)}</p>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">
                          Price History — {ph.part_name}
                          <span className="text-sm font-normal text-zinc-500 ml-2">{ph.total_versions} version{ph.total_versions !== 1 ? 's' : ''}</span>
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis dataKey="date" stroke={axisColor} tick={{ fontSize: 10, fill: labelColor }} />
                            <YAxis stroke={axisColor} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                            <Tooltip content={<CustomTooltip isDark={isDark} />} />
                            <Legend />
                            <Line type="monotone" dataKey="DBP" stroke="#C3002F" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="MRP" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="COGS" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
                        <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
                          <h3 className="text-base font-semibold text-zinc-900 dark:text-white">Version History</h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Version</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Date</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Currency</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">DBP</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">MRP</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">COGS</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">GP%</th>
                                <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Approved By</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ph.price_history.map((h, i) => (
                                <tr key={i} className="border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
                                  <td className="py-3 px-4 font-mono text-zinc-500">v{h.version}</td>
                                  <td className="py-3 px-4 text-zinc-700 dark:text-zinc-300">{h.date}</td>
                                  <td className="py-3 px-4 font-mono text-zinc-500">INR</td>
                                  <td className="py-3 px-4 font-mono text-[#C3002F]">{fmtPrice(h.dbp)}</td>
                                  <td className="py-3 px-4 font-mono text-amber-600 dark:text-amber-400">{fmtPrice(h.mrp)}</td>
                                  <td className="py-3 px-4 font-mono text-purple-600 dark:text-purple-400">{fmtPrice(h.cogs)}</td>
                                  <td className="py-3 px-4 font-mono text-emerald-600 dark:text-emerald-400">{fmtPct(h.gp_percent)}</td>
                                  <td className="py-3 px-4 text-zinc-600 dark:text-zinc-400">{h.approved_by}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Competitor Pricing */}
                {selectedPart && (
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-cyan-500" />
                        Competitor Pricing
                        {competitorPricing?.total_competitors != null && !competitorLoading && (
                          <span className="text-sm font-normal text-zinc-500">({competitorPricing.total_competitors} competitor{competitorPricing.total_competitors !== 1 ? 's' : ''})</span>
                        )}
                      </h3>
                      {competitorPricing?.our_price != null && !competitorLoading && (
                        <span className="text-xs text-zinc-500">Our price: <span className="font-mono font-semibold text-[#C3002F]">{fmtPrice(competitorPricing.our_price)}</span></span>
                      )}
                    </div>
                    {competitorLoading ? (
                      <div className="flex items-center gap-3 py-6 text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin text-[#C3002F]" />
                        <span className="text-sm">Fetching competitor pricing, this may take a moment...</span>
                      </div>
                    ) : competitorPricing?.competitors?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/60">
                              <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Competitor Vehicle</th>
                              <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Part</th>
                              <th className="text-right py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Competitor Price</th>
                              <th className="text-right py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Difference</th>
                              {/* <th className="text-center py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Confidence</th> */}
                              {/* <th className="py-2 px-3" /> */}
                            </tr>
                          </thead>
                          <tbody>
                            {competitorPricing.competitors.map((c, i) => {
                              const ourPrice = competitorPricing.our_price;
                              const diff = ourPrice != null && c.competitor_price != null ? ourPrice - c.competitor_price : null;
                              const diffPct = diff != null && c.competitor_price ? (diff / c.competitor_price) * 100 : null;
                              return (
                                <tr key={c.competitor_id ?? i} className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
                                  <td className="py-2 px-3 text-zinc-900 dark:text-white font-medium">
                                    {c.competitor_vehicle}
                                    {c.competitor_vehicle_type && <span className="ml-1 text-xs text-zinc-400">({c.competitor_vehicle_type})</span>}
                                  </td>
                                  <td className="py-2 px-3 text-zinc-600 dark:text-zinc-400">{c.competitor_part_name}</td>
                                  <td className="py-2 px-3 text-right font-mono text-cyan-600 dark:text-cyan-400">{fmtPrice(c.competitor_price)}</td>
                                  <td className="py-2 px-3 text-right">
                                    {diff != null ? (
                                      <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${diff >= 0 ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'}`}>
                                        {diff >= 0 ? '+' : ''}{fmtPct(diffPct, 1)}
                                      </span>
                                    ) : '—'}
                                  </td>
                                  {/* <td className="py-2 px-3 text-center">
                                    <span className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${c.confidence_score >= 0.8 ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : c.confidence_score >= 0.5 ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-500'}`}>
                                      {c.confidence_score != null ? `${Math.round(c.confidence_score * 100)}%` : '—'}
                                    </span>
                                  </td> */}
                                  {/* <td className="py-2 px-3">
                                    {c.source_urls?.[0] && (
                                      <a href={c.source_urls[0]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 hover:underline whitespace-nowrap">
                                        View <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </td> */}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-500 py-4">No competitor pricing data available for this part.</p>
                    )}
                  </div>
                )}

                {/* Part Details */}
                {partDetails && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-zinc-900 dark:text-white border-t border-zinc-200 dark:border-zinc-800 pt-6">
                      Part Details — {partDetails.part_name}
                      <span className="ml-2 text-sm font-mono font-normal text-zinc-500">{partDetails.part_number}</span>
                    </h3>

                    {(() => {
                      return (<>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                        <p className="text-xs text-zinc-500 uppercase mb-1">Current Price (DBP)</p>
                        <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{fmtPrice(partDetails.statistics?.current_price)}</p>
                      </div>
                      <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                        <p className="text-xs text-zinc-500 uppercase mb-1">Current MRP</p>
                        <p className="text-xl font-mono font-bold text-amber-600 dark:text-amber-400">{fmtPrice(partDetails.statistics?.current_mrp)}</p>
                      </div>
                      <div className="p-4 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded-lg">
                        <p className="text-xs text-cyan-600 dark:text-cyan-400 uppercase mb-1">Avg GP%</p>
                        <p className="text-xl font-mono font-bold text-cyan-600 dark:text-cyan-400">{fmtPct(partDetails.statistics?.avg_gp_percent)}</p>
                      </div>
                      <div className="p-4 bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 rounded-lg">
                        <p className="text-xs text-purple-600 dark:text-purple-400 uppercase mb-1">Price Volatility</p>
                        <p className="text-xl font-mono font-bold text-purple-600 dark:text-purple-400">{fmtPrice(partDetails.statistics?.price_volatility)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 uppercase mb-1">Min Price</p>
                        <p className="text-xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{fmtPrice(partDetails.statistics?.min_price)}</p>
                      </div>
                      <div className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                        <p className="text-xs text-red-600 dark:text-red-400 uppercase mb-1">Max Price</p>
                        <p className="text-xl font-mono font-bold text-red-600 dark:text-red-400">{fmtPrice(partDetails.statistics?.max_price)}</p>
                      </div>
                      <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                        <p className="text-xs text-zinc-500 uppercase mb-1">Avg Price (DBP)</p>
                        <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{fmtPrice(partDetails.statistics?.avg_price)}</p>
                      </div>
                      <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
                        <p className="text-xs text-amber-600 dark:text-amber-400 uppercase mb-1">Avg MRP</p>
                        <p className="text-xl font-mono font-bold text-amber-600 dark:text-amber-400">{fmtPrice(partDetails.statistics?.avg_mrp)}</p>
                      </div>
                      <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                        <p className="text-xs text-zinc-500 uppercase mb-1">Approved Versions</p>
                        <p className="text-xl font-mono font-bold text-zinc-900 dark:text-white">{partDetails.statistics?.total_approved_versions ?? '-'}</p>
                      </div>
                    </div>

                    {partDetails.price_timeline?.length > 0 && (
                      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                        <h4 className="text-base font-semibold text-zinc-900 dark:text-white mb-4">
                          Price Timeline
                          <span className="text-sm font-normal text-zinc-500 ml-2">{partDetails.price_timeline.length} version{partDetails.price_timeline.length !== 1 ? 's' : ''}</span>
                        </h4>
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={partDetails.price_timeline.map(h => ({ date: `v${h.version} · ${h.date}`, DBP: h.dbp, MRP: h.mrp, COGS: h.cogs, 'GP%': h.gp_percent }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis dataKey="date" stroke={axisColor} tick={{ fontSize: 10, fill: labelColor }} />
                            <YAxis yAxisId="price" stroke={axisColor} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                            <YAxis yAxisId="pct" orientation="right" stroke={axisColor} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                            <Tooltip content={<CustomTooltip isDark={isDark}  />} />
                            <Legend />
                            <Line yAxisId="price" type="monotone" dataKey="DBP" stroke="#C3002F" strokeWidth={2} dot={{ r: 4 }} />
                            <Line yAxisId="price" type="monotone" dataKey="MRP" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                            <Line yAxisId="price" type="monotone" dataKey="COGS" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
                            <Line yAxisId="pct" type="monotone" dataKey="GP%" stroke="#10b981" strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                        <h4 className="text-base font-semibold text-zinc-900 dark:text-white mb-3">Part Information</h4>
                        <dl className="space-y-2">
                          {[
                            ['Description', partDetails.part_details?.description],
                            ['Family', partDetails.family_name],
                            ['Supplier', partDetails.part_details?.supplier],
                            ['Vendor Code', partDetails.part_details?.vendor_code],
                            ['Currency', 'INR'],
                            ['Purchase Price (SAP)', fmtPrice(partDetails.part_details?.sap_purchase_price)],
                            ['Lead Time', partDetails.part_details?.lead_time_days != null ? `${partDetails.part_details.lead_time_days} days` : '-'],
                            ['Min Order Qty', partDetails.part_details?.min_order_qty ?? '-'],
                          ].map(([key, val]) => (
                            <div key={key} className="flex justify-between py-1 border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                              <dt className="text-xs text-zinc-500 uppercase">{key}</dt>
                              <dd className="text-sm font-medium text-zinc-800 dark:text-zinc-200 text-right max-w-[60%]">{val ?? '—'}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>

                      {partDetails.latest_card && (
                        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg">
                          <h4 className="text-base font-semibold text-zinc-900 dark:text-white mb-3">Latest Card Breakdown</h4>
                          <dl className="space-y-2">
                            {[
                              ['Template', partDetails.latest_card.template_name],
                              ['Currency', 'INR'],
                              ['Purchase Price INR', fmtPrice(partDetails.latest_card.purchase_price_inr)],
                              ['Customs Duty', `${partDetails.latest_card.customs_duty_percent ?? '-'}% (${fmtPrice(partDetails.latest_card.customs_amount)})`],
                              ['ISC', `${partDetails.latest_card.isc_percent ?? '-'}% (${fmtPrice(partDetails.latest_card.isc_amount)})`],
                              ['COGS', fmtPrice(partDetails.latest_card.cogs)],
                              ['GP', `${partDetails.latest_card.gp_percent ?? '-'}% (${fmtPrice(partDetails.latest_card.gp_amount)})`],
                              ['DBP', fmtPrice(partDetails.latest_card.dbp)],
                              ['DDR%', partDetails.latest_card.ddr_percent != null ? `${partDetails.latest_card.ddr_percent}%` : '-'],
                              ['MRP', fmtPrice(partDetails.latest_card.mrp)],
                              ['CBP', fmtPrice(partDetails.latest_card.cbp)],
                              ['Dealer Margin', `${partDetails.latest_card.dealer_margin_percent ?? '-'}% (${fmtPrice(partDetails.latest_card.dealer_margin)})`],
                              ['Status', partDetails.latest_card.status],
                              ['Approved By', partDetails.latest_card.approved_by_name],
                            ].map(([key, val]) => (
                              <div key={key} className="flex justify-between py-1 border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                                <dt className="text-xs text-zinc-500 uppercase">{key}</dt>
                                <dd className="text-sm font-medium text-zinc-800 dark:text-zinc-200 text-right max-w-[60%]">{val ?? '—'}</dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      )}
                    </div>

                    {partDetails.super_seeded_info && (
                      <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 p-4 rounded-lg">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          <span className="font-semibold">Super Seeded:</span> This part has been super seeded by{' '}
                          <span className="font-mono">{partDetails.super_seeded_info.super_seeded_part_number}</span> with an adjustment of {partDetails.super_seeded_info.super_seeded_adjustment}%
                        </p>
                      </div>
                    )}
                  </>); })()}
                  </div>
                )}
              </div>
            </div>

            {/* Top Parts Table */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-4">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                  Top Parts By Sale
                  {topParts.length > 0 && <span className="ml-2 text-sm font-normal text-zinc-500">({topParts.length} total)</span>}
                </h3>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500">Show</label>
                  <select
                    value={topPartsPageSize}
                    onChange={e => { setTopPartsPageSize(Number(e.target.value)); setTopPartsPage(1); }}
                    className="h-8 px-2 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C3002F]/30"
                  >
                    {[5, 10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <label className="text-xs text-zinc-500">per page</label>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Rank</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Part Number</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Part Name</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Family</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Units Sold</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">MRP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPartsSlice.length > 0 ? topPartsSlice.map((part, index) => {
                      const rank = (topPartsPage - 1) * topPartsPageSize + index + 1;
                      return (
                        <tr key={part.part_number} className="border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
                          <td className="py-3 px-4 text-zinc-500 dark:text-zinc-400 font-bold">{rank}</td>
                          <td className="py-3 px-4 font-mono text-zinc-900 dark:text-white">{part.part_number}</td>
                          <td className="py-3 px-4 text-zinc-700 dark:text-zinc-300">{part.part_name}</td>
                          <td className="py-3 px-4 text-zinc-600 dark:text-zinc-400">{part.family_name}</td>
                          <td className="py-3 px-4 font-mono text-amber-600 dark:text-amber-400">{part.units_sold?.toLocaleString()}</td>
                          <td className="py-3 px-4 font-mono text-emerald-600 dark:text-emerald-400">{fmtPrice(part.mrp)}</td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-zinc-500">No approved parts data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {topPartsTotalPages > 1 && (
                <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-4">
                  <p className="text-sm text-zinc-500">
                    Showing {(topPartsPage - 1) * topPartsPageSize + 1}–{Math.min(topPartsPage * topPartsPageSize, topParts.length)} of {topParts.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setTopPartsPage(1)} disabled={topPartsPage === 1} className="px-2 py-1 text-sm rounded border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 hover:bg-zinc-100 dark:hover:bg-zinc-800">«</button>
                    <button onClick={() => setTopPartsPage(p => p - 1)} disabled={topPartsPage === 1} className="px-2 py-1 text-sm rounded border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 hover:bg-zinc-100 dark:hover:bg-zinc-800">‹</button>
                    <span className="px-3 py-1 text-sm text-zinc-700 dark:text-zinc-300">{topPartsPage} / {topPartsTotalPages}</span>
                    <button onClick={() => setTopPartsPage(p => p + 1)} disabled={topPartsPage === topPartsTotalPages} className="px-2 py-1 text-sm rounded border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 hover:bg-zinc-100 dark:hover:bg-zinc-800">›</button>
                    <button onClick={() => setTopPartsPage(topPartsTotalPages)} disabled={topPartsPage === topPartsTotalPages} className="px-2 py-1 text-sm rounded border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 hover:bg-zinc-100 dark:hover:bg-zinc-800">»</button>
                  </div>
                </div>
              )}
            </div>


          </div>
        )}
      </div>
    </MainLayout>
  );
}
